// Applies a business's chosen brand color to Element Plus's own theming
// variables, so <el-button type="primary">, links, tags, switches etc.
// everywhere in the app pick it up automatically - Element Plus's compiled
// CSS reads --el-color-primary (and a fixed set of derived light/dark
// shades) rather than computing them itself, so those shades have to be
// computed here and set alongside the base color. Mirrors the mix-with-
// white/black formula Element Plus's own SCSS theme uses (light-N = N*10%
// white, dark-2 = 20% black), just done at runtime instead of build time.
//
// Also overrides --bs-menu-link-color-active/-here - the two variables
// the compiled Metronic bundle's sidebar CSS reads for the currently
// active/"here" nav item's text+icon color (see style.bundle.css's
// [data-bs-theme=light/dark] blocks). Unlike its .btn-primary rules, which
// hardcode colors directly on the class instead of referencing a
// variable, these two are genuine custom properties, so overriding them
// here is enough on its own - no extra CSS needed.
const LIGHT_LEVELS = [3, 5, 7, 8, 9];
const MENU_ACTIVE_VAR_NAMES = ['--bs-menu-link-color-active', '--bs-menu-link-color-here'];

// Dark/light mode toggling (see TopHeader.vue's toggleAppMode) always does
// a full page reload rather than a reactive in-place switch, so a plain
// one-time DOM read here - not a watcher - is enough; anything that calls
// this always re-evaluates fresh on whichever mode is currently active.
function isAppInDarkMode() {
    return document.documentElement.getAttribute('data-bs-theme') === 'dark';
}

function hexToRgb(hex) {
    const clean = hex.replace('#', '');
    return {
        r: parseInt(clean.substring(0, 2), 16),
        g: parseInt(clean.substring(2, 4), 16),
        b: parseInt(clean.substring(4, 6), 16),
    };
}

function rgbToHex({r, g, b}) {
    const toHex = (n) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mix(c1, c2, weight) {
    return {
        r: c1.r * weight + c2.r * (1 - weight),
        g: c1.g * weight + c2.g * (1 - weight),
        b: c1.b * weight + c2.b * (1 - weight),
    };
}

const VAR_NAMES = [
    '--el-color-primary',
    '--el-color-primary-rgb',
    ...LIGHT_LEVELS.map((level) => `--el-color-primary-light-${level}`),
    '--el-color-primary-dark-2',
    ...MENU_ACTIVE_VAR_NAMES,
];

export function computeBrandColorVars(hex) {
    const base = hexToRgb(hex);
    const white = {r: 255, g: 255, b: 255};
    const black = {r: 0, g: 0, b: 0};

    const vars = {
        '--el-color-primary': hex,
        '--el-color-primary-rgb': `${base.r}, ${base.g}, ${base.b}`,
        '--el-color-primary-dark-2': rgbToHex(mix(black, base, 0.2)),
        '--bs-menu-link-color-active': hex,
        '--bs-menu-link-color-here': hex,
    };
    LIGHT_LEVELS.forEach((level) => {
        vars[`--el-color-primary-light-${level}`] = rgbToHex(mix(white, base, level * 0.1));
    });
    return vars;
}

export function applyBrandColorVars(hex) {
    if (!hex) {
        clearBrandColorVars();
        return;
    }
    const vars = computeBrandColorVars(hex);
    if (isAppInDarkMode()) {
        // The sidebar - the only actual consumer of these two variables
        // in this app's fixed dark-sidebar layout (see style.bundle.css)
        // - should stay Kodi's own dark-mode look untouched by business
        // branding in dark mode (see Sidebar.vue's own isDarkMode-gated
        // computeds for its background/text colors). Element Plus's own
        // vars are left alone here - buttons/links elsewhere in the app
        // keep the brand color regardless of theme, only the sidebar is
        // theme-gated.
        MENU_ACTIVE_VAR_NAMES.forEach((name) => delete vars[name]);
    }
    Object.entries(vars).forEach(([name, value]) => {
        document.documentElement.style.setProperty(name, value);
    });
}

export function clearBrandColorVars() {
    VAR_NAMES.forEach((name) => document.documentElement.style.removeProperty(name));
}
